import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  Grid,
  Autocomplete
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../../services/ApiService';

interface Requirement {
  id: string;
  title: string;
}

interface DocumentUploadProps {
  onUploadComplete?: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ onUploadComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other' as 'policy' | 'template' | 'evidence' | 'report' | 'other',
    uploadedBy: '',
    requirementId: '',
    requirementTitle: '',
    tags: [] as string[]
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newTag, setNewTag] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchRequirements();
  }, []);

  const fetchRequirements = async () => {
    try {
      const response = await ApiService.get<Requirement[]>('/requirements');
      setRequirements(response.map((req: any) => ({
        id: req.id.toString(),
        title: req.title
      })));
    } catch (error) {
      console.error('Error fetching requirements:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (error) setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!formData.title) {
        // Auto-fill title with filename (without extension)
        const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
        setFormData({ ...formData, title: titleFromFile });
      }
    }
  };

  const handleRequirementChange = (requirement: Requirement | null) => {
    if (requirement) {
      setFormData({
        ...formData,
        requirementId: requirement.id,
        requirementTitle: requirement.title
      });
    } else {
      setFormData({
        ...formData,
        requirementId: '',
        requirementTitle: ''
      });
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const validateForm = () => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.description.trim()) return 'Description is required';
    if (!selectedFile) return 'Please select a file to upload';
    if (!formData.uploadedBy.trim()) return 'Uploaded by field is required';
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate file upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Prepare document data
      const documentData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        fileName: selectedFile!.name,
        fileSize: selectedFile!.size,
        fileType: selectedFile!.type || 'application/octet-stream',
        category: formData.category,
        uploadedBy: formData.uploadedBy.trim(),
        requirementId: formData.requirementId || undefined,
        requirementTitle: formData.requirementTitle || undefined,
        tags: formData.tags
      };

      // Create FormData for file upload
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile!);
      uploadFormData.append('title', documentData.title);
      uploadFormData.append('description', documentData.description);
      uploadFormData.append('category', documentData.category);
      uploadFormData.append('uploadedBy', documentData.uploadedBy);
      if (documentData.requirementId) {
        uploadFormData.append('requirementId', documentData.requirementId);
      }
      uploadFormData.append('tags', JSON.stringify(documentData.tags));
      
      // Upload document with file
      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        body: uploadFormData
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await response.json();
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        category: 'other',
        uploadedBy: '',
        requirementId: '',
        requirementTitle: '',
        tags: []
      });
      setSelectedFile(null);
      
      if (onUploadComplete) {
        onUploadComplete();
      }
      
      // Navigate back to documents list after a short delay
      setTimeout(() => {
        navigate('/documents');
      }, 2000);
      
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload document');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/documents');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Upload Document
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Document uploaded successfully! Redirecting to documents list...
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* File Upload Section */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    hidden
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {selectedFile ? selectedFile.name : 'Click to select a file'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, Images
                  </Typography>
                  {selectedFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Upload Progress */}
              {loading && uploadProgress > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Uploading... {uploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                </Grid>
              )}

              {/* Document Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Document Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  error={error === 'Title is required'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="policy">Policy</MenuItem>
                    <MenuItem value="template">Template</MenuItem>
                    <MenuItem value="evidence">Evidence</MenuItem>
                    <MenuItem value="report">Report</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  required
                  error={error === 'Description is required'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Uploaded By"
                  value={formData.uploadedBy}
                  onChange={(e) => handleInputChange('uploadedBy', e.target.value)}
                  required
                  error={error === 'Uploaded by field is required'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={requirements}
                  getOptionLabel={(option) => option.title}
                  value={requirements.find(req => req.id === formData.requirementId) || null}
                  onChange={(_, value) => handleRequirementChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link to Requirement (Optional)"
                      placeholder="Search requirements..."
                    />
                  )}
                />
              </Grid>

              {/* Tags Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      deleteIcon={<CloseIcon />}
                      size="small"
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    label="Add Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    color="primary"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>

              {/* Error Message */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !selectedFile}
                    startIcon={loading ? undefined : <CloudUploadIcon />}
                  >
                    {loading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentUpload;

